/**
 * Sidebar header: logo, collapse/expand toggle, and country identity block.
 * Shown at the top of the sidebar for all users.
 */

import { Box, IconButton, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { COLORS } from '../layoutConstants';

interface SidebarHeaderProps {
  sidebarExpanded: boolean;
  isMobile: boolean;
  onDrawerToggle: () => void;
  /** Admin country name — shown only for country admins when sidebar is expanded. */
  countryName?: string | null;
  /** Admin country flag emoji — shown alongside country name. */
  countryFlagEmoji?: string | null;
  /** Whether to show the country identity block (country admin only). */
  isCountryAdmin: boolean;
}

export function SidebarHeader({
  sidebarExpanded,
  isMobile,
  onDrawerToggle,
  countryName,
  countryFlagEmoji,
  isCountryAdmin,
}: SidebarHeaderProps) {
  return (
    <>
      {/* Logo area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarExpanded ? 'space-between' : 'center',
          py: '28px',
          px: sidebarExpanded ? 2.5 : 1,
          borderBottom: `1px solid ${COLORS.sidebarBorder}`,
          flexShrink: 0,
        }}
      >
        {sidebarExpanded ? (
          <Box
            component="img"
            src="/logo-light.png"
            alt="Terra17"
            sx={{ height: 40, objectFit: 'contain' }}
          />
        ) : (
          <Box
            component="img"
            src="/favicon-light.svg"
            alt="Terra17"
            sx={{ height: 32, objectFit: 'contain' }}
          />
        )}
        {sidebarExpanded && (
          <IconButton
            onClick={onDrawerToggle}
            size="small"
            sx={{
              color: COLORS.navText,
              '&:hover': { color: COLORS.navTextHover, backgroundColor: COLORS.sidebarHoverBg },
            }}
            aria-label={isMobile ? 'close menu' : 'collapse sidebar'}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Expand toggle when collapsed (desktop only) */}
      {!isMobile && !sidebarExpanded && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1.5,
            pb: 0.5,
            flexShrink: 0,
          }}
        >
          <IconButton
            onClick={onDrawerToggle}
            size="small"
            sx={{
              color: COLORS.navText,
              '&:hover': {
                color: COLORS.navTextHover,
                backgroundColor: COLORS.sidebarHoverBg,
              },
            }}
            aria-label="expand sidebar"
          >
            <MenuIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Country identity block (country admin only, expanded sidebar) */}
      {isCountryAdmin && countryName && sidebarExpanded && (
        <Box
          sx={{
            mx: 1.5,
            mt: 1.5,
            mb: 0.5,
            px: 2,
            py: 1.5,
            borderRadius: '10px',
            background:
              'linear-gradient(135deg, rgba(107, 63, 160, 0.15) 0%, rgba(70, 95, 255, 0.10) 100%)',
            border: '1px solid rgba(107, 63, 160, 0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            flexShrink: 0,
          }}
        >
          {countryFlagEmoji && (
            <Typography sx={{ fontSize: 32, lineHeight: 1 }}>
              {countryFlagEmoji}
            </Typography>
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: '#FFFFFF',
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {countryName}
            </Typography>
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 600,
                color: COLORS.sectionHeader,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Admin
            </Typography>
          </Box>
        </Box>
      )}
    </>
  );
}
